import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Release } from 'types/releases';
import { useInfiniteLoader } from 'app/utils/hooks';
import { getReleaseListNextLink } from 'app/store/Releases/selectors';
import { getReleasesListEffect } from 'app/store/Releases/effects';
import { ChakraEmptyList } from 'app/components/Core';
import { ReactComponent as ReleaseIcon } from 'resources/images/group.svg';
import {
  getOpenInNewTabMenuOption,
  useContextMenu,
} from 'app/components/ChakraTableList';
import { routes } from 'app/routing/routesConfig';
import { DeleteReleaseDialog } from 'app/components/Releases/DeleteReleaseDialog';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { Row } from '@tanstack/react-table';
import { contextMenuHandler } from 'app/components/Table/ContextMenuButton/utils';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { getColumns } from './utils';
import { ReleaseRow } from './ReleaseRow';

export interface ReleaseListProps {
  loading: boolean;
  releases: Release[];
  canDeleteRelease: boolean;
}

export const ReleaseListComponent: React.FC<ReleaseListProps> = ({
  releases,
  loading,
  canDeleteRelease,
}) => {
  const [deleteRelease, setDeleteRelease] = useState<Release>();
  const [, infiniteScrollProps, loadingMore] = useInfiniteLoader({
    dataLength: releases.length,
    nextLinkSelector: getReleaseListNextLink,
    fetchNext: (link) => getReleasesListEffect({ next: link }),
  });

  const contextMenuProps = useContextMenu();
  const rowRenderer = useCallback(
    (item: Row<Release>) => {
      const release = item.original;
      const redirectUrl = routes.release.path.replace(':guid', release.guid);

      const options = [
        ...getOpenInNewTabMenuOption(redirectUrl),
        ...(canDeleteRelease
          ? [
              {
                title: 'Delete',
                value: 'DELETE',
                onClick: () => setDeleteRelease(release),
              },
            ]
          : []),
      ];

      return (
        <ReleaseRow
          key={release.guid}
          release={release}
          contextHandler={(e) =>
            contextMenuHandler(e, contextMenuProps, options)
          }
        />
      );
    },
    [canDeleteRelease, contextMenuProps]
  );

  const ref = useRef<HTMLDivElement>(null);
  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(() => getColumns(totalWidth), [totalWidth]);

  if (!loading && releases.length === 0) {
    return (
      <ChakraEmptyList
        icon={<ReleaseIcon role="img" />}
        title="Deliver Your Songs"
        primaryText="Send songs and submit label copy to music industry partners, including record labels. We collect data in an industry-approved format to save you switching platforms, and moving files around."
      />
    );
  }

  const tableKey = 'releases';

  return (
    <>
      <StickyTable<Release>
        tableKey={tableKey}
        ref={ref}
        data={releases}
        columns={columns}
        infiniteScrollProps={infiniteScrollProps}
        loadingMore={loadingMore}
        rowRenderer={rowRenderer}
        contextMenuProps={contextMenuProps}
        loading={loading}
      />
      <DeleteReleaseDialog
        release={deleteRelease}
        onClose={() => setDeleteRelease(undefined)}
      />
    </>
  );
};
